import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Btn, fmtBytes, fmtDate } from './parts';
import { useToast } from '@/lib/ui';
import { useAuth } from '@/hooks/useAuth';
import { useConfirmedMutation } from '@/api/mutations';
import { chunkCompressFn, chunksMergeFn, chunkSplitFn } from '@/api/endpoints';

export type Chunk = {
  name: string;
  size_bytes: number;
  index_bytes: number;
  range_start: string;
  range_end: string;
  compression_pct: number;
  decompressed_bytes: number;
  compressed: boolean;
};

type SortKey = 'name' | 'size_bytes' | 'range_start' | 'compression_pct';
type SortDir = 'asc' | 'desc';

export function ChunkTable({ chunks, hypertable }: { chunks: Chunk[]; hypertable: string }) {
  const toast = useToast();
  const qc = useQueryClient();
  const { user: me } = useAuth();
  const isSuperAdmin = me?.role === 'superAdmin';
  const invalidate = () => qc.invalidateQueries({ queryKey: ['db'] });
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('range_start');
  const [sortDir, setSortDir] = useState<SortDir>('asc'); // oldest → newest by default
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [splitDialog, setSplitDialog] = useState<{ chunk: string } | null>(null);
  const [splitAt, setSplitAt] = useState('');

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    let r = chunks.filter((c) => !s || c.name.toLowerCase().includes(s));
    r = [...r].sort((a, b) => {
      const dir = sortDir === 'asc' ? 1 : -1;
      const av = a[sortKey];
      const bv = b[sortKey];
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
      return String(av).localeCompare(String(bv)) * dir;
    });
    return r;
  }, [chunks, search, sortKey, sortDir]);

  const toggleSort = (k: SortKey) => {
    if (sortKey === k) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(k);
      setSortDir(k === 'name' ? 'asc' : 'desc');
    }
  };

  const toggleRow = (name: string) => {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map((c) => c.name)));
  };

  const mergeMut = useConfirmedMutation(
    (names: string[]) => chunksMergeFn(names),
    {
      title: 'Fusionner les chunks',
      message: 'Confirmer le merge des chunks sélectionnés ? (irréversible)',
      destructive: true,
      onSuccess: () => { toast.push('Chunks fusionnés', 'success'); setSelected(new Set()); invalidate(); },
    },
  );

  const splitMut = useConfirmedMutation(
    (vars: { chunk: string; at: string }) => chunkSplitFn(vars.chunk, vars.at),
    {
      title: 'Diviser le chunk',
      message: 'Confirmer le split de ce chunk ?',
      destructive: true,
      onSuccess: () => { toast.push('Chunk divisé', 'success'); setSplitDialog(null); setSplitAt(''); invalidate(); },
    },
  );

  const compressMut = useConfirmedMutation(
    (vars: { chunk: string; compress: boolean }) => chunkCompressFn(vars.chunk, vars.compress),
    {
      title: 'Compression chunk',
      message: 'Confirmer l’opération de (dé)compression ?',
      onSuccess: (_r, vars) => { toast.push(vars.compress ? 'Chunk compressé' : 'Chunk décompressé', 'success'); invalidate(); },
    },
  );

  const compressBatchMut = useConfirmedMutation(
    (vars: { names: string[]; compress: boolean }) =>
      Promise.allSettled(vars.names.map((n) => chunkCompressFn(n, vars.compress))).then((rs) => ({ rs, compress: vars.compress })),
    {
      title: 'Compression de chunks',
      message: 'Confirmer la (dé)compression des chunks sélectionnés ?',
      destructive: true,
      onSuccess: (res) => {
        const ok = res.rs.filter((r) => r.status === 'fulfilled').length;
        const ko = res.rs.length - ok;
        toast.push(`${ok} chunk(s) ${res.compress ? 'compressé(s)' : 'décompressé(s)'}${ko ? ` · ${ko} échec` : ''}`, ko ? 'warn' : 'success');
        setSelected(new Set());
        invalidate();
      },
    },
  );

  const onMerge = () => mergeMut.mutate([...selected]);
  const onCompressBatch = (compress: boolean) => compressBatchMut.mutate({ names: [...selected], compress });
  const onSplit = () => splitDialog && splitMut.mutate({ chunk: splitDialog.chunk, at: splitAt });
  const onCompress = (chunk: string, currentlyCompressed: boolean) => {
    setOpenMenu(null);
    compressMut.mutate({ chunk, compress: !currentlyCompressed });
  };

  const SortHead = ({ k, label, align }: { k: SortKey; label: string; align?: 'right' }) => (
    <th
      className={`px-3 py-2 ${align === 'right' ? 'text-right' : 'text-left'} cursor-pointer select-none hover:bg-muted`}
      onClick={() => toggleSort(k)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {sortKey === k && <span className="text-[10px]">{sortDir === 'asc' ? '↑' : '↓'}</span>}
      </span>
    </th>
  );

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="🔍 Rechercher chunk..."
          className="h-9 px-3 text-sm rounded-md border border-input bg-background flex-1 min-w-[200px]"
        />
        {selected.size >= 1 && isSuperAdmin && (
          <>
            <span className="text-xs text-muted-foreground">{selected.size} sélectionné(s)</span>
            {selected.size >= 2 && (
              <Btn size="sm" variant="destructive" onClick={onMerge}>
                Fusionner
              </Btn>
            )}
            <Btn size="sm" variant="default" onClick={() => onCompressBatch(true)}>
              Comprimer
            </Btn>
            <Btn size="sm" variant="default" onClick={() => onCompressBatch(false)}>
              Décomprimer
            </Btn>
            <Btn size="sm" variant="ghost" onClick={() => setSelected(new Set())}>
              ✕
            </Btn>
          </>
        )}
      </div>

      <div className="border border-border rounded-lg overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-muted/50 text-[11px] uppercase text-muted-foreground">
            <tr>
              <th className="w-8 px-3 py-2">
                <input
                  type="checkbox"
                  checked={selected.size > 0 && selected.size === filtered.length}
                  onChange={toggleAll}
                  ref={(el) => {
                    if (el) el.indeterminate = selected.size > 0 && selected.size < filtered.length;
                  }}
                />
              </th>
              <SortHead k="name" label="Name" />
              <SortHead k="size_bytes" label="Size" align="right" />
              <th className="text-right px-3 py-2">Index</th>
              <SortHead k="range_start" label="Start" />
              <th className="text-left px-3 py-2">End</th>
              <SortHead k="compression_pct" label="Compression" align="right" />
              <th className="text-right px-3 py-2">Décompressé</th>
              <th className="w-12 px-3 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {filtered.map((c) => (
              <tr key={c.name} className={selected.has(c.name) ? 'bg-primary/5' : 'hover:bg-accent/30'}>
                <td className="px-3 py-2">
                  <input type="checkbox" checked={selected.has(c.name)} onChange={() => toggleRow(c.name)} />
                </td>
                <td className="px-3 py-2 font-mono">{c.name}</td>
                <td className="px-3 py-2 text-right font-mono">{fmtBytes(c.size_bytes)}</td>
                <td className="px-3 py-2 text-right font-mono text-muted-foreground">{fmtBytes(c.index_bytes)}</td>
                <td className="px-3 py-2 font-mono text-muted-foreground">{fmtDate(c.range_start)}</td>
                <td className="px-3 py-2 font-mono text-muted-foreground">{fmtDate(c.range_end)}</td>
                <td className="px-3 py-2 text-right font-mono">
                  {c.compressed ? (
                    <span className="text-green-700 dark:text-green-400">{c.compression_pct.toFixed(1)}%</span>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </td>
                <td className="px-3 py-2 text-right font-mono text-muted-foreground">
                  {fmtBytes(c.decompressed_bytes)}
                </td>
                <td className="px-3 py-2 text-right relative">
                  {isSuperAdmin && (
                  <button
                    className="w-7 h-7 rounded hover:bg-accent inline-flex items-center justify-center"
                    onClick={() => setOpenMenu(openMenu === c.name ? null : c.name)}
                    aria-label="Actions"
                  >
                    ⋯
                  </button>
                  )}
                  {isSuperAdmin && openMenu === c.name && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setOpenMenu(null)} />
                      <div className="absolute right-2 top-9 z-20 bg-card border border-border rounded-md shadow-lg py-1 min-w-[180px] text-left">
                        <button
                          className="w-full px-3 py-1.5 text-xs hover:bg-accent text-left"
                          onClick={() => {
                            setOpenMenu(null);
                            setSplitDialog({ chunk: c.name });
                            setSplitAt(c.range_start.slice(0, 16));
                          }}
                        >
                          ✂ Split (superAdmin)
                        </button>
                        <button
                          className="w-full px-3 py-1.5 text-xs hover:bg-accent text-left"
                          onClick={() => onCompress(c.name, c.compressed)}
                        >
                          {c.compressed ? '📂 Décompresser (superAdmin)' : '🗜 Compresser (superAdmin)'}
                        </button>
                      </div>
                    </>
                  )}
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={9} className="px-3 py-6 text-center text-muted-foreground text-xs">
                  Aucun chunk
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {splitDialog && (
        <div className="fixed inset-0 z-[150] flex items-center justify-center p-4 bg-black/50" onClick={() => setSplitDialog(null)}>
          <div className="bg-card border border-border rounded-lg shadow-2xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-border">
              <h3 className="text-lg font-bold">Split chunk</h3>
              <p className="text-xs text-muted-foreground mt-1 font-mono">{splitDialog.chunk}</p>
            </div>
            <div className="px-5 py-4 space-y-2">
              <label className="text-sm font-medium">Point de split (timestamp)</label>
              <input
                type="datetime-local"
                value={splitAt}
                onChange={(e) => setSplitAt(e.target.value)}
                className="w-full h-9 px-3 text-sm rounded-md border border-input bg-background"
              />
              <p className="text-xs text-muted-foreground">
                Le chunk sera divisé en deux à ce timestamp (doit être dans la plage du chunk).
              </p>
            </div>
            <div className="px-5 py-3 border-t border-border flex justify-end gap-2">
              <Btn onClick={() => setSplitDialog(null)}>Annuler</Btn>
              <Btn variant="destructive" onClick={onSplit} disabled={!splitAt}>
                Split (superAdmin)
              </Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
